import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import './CheckoutPage.css';

const CheckoutForm = ({ booking, amount, onSuccess, setBooking }) => {
  const { currentUser } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentGateway, setPaymentGateway] = useState('paypro');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showBankDetails, setShowBankDetails] = useState(false);

  useEffect(() => {
    // Set customer info from current user or booking
    if (currentUser) {
      setCustomerInfo({
        name: currentUser.displayName || currentUser.full_name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      });
    } else if (booking) {
      setCustomerInfo({
        name: booking.guest_name || '',
        email: booking.guest_email || '',
        phone: booking.guest_phone || ''
      });
    }
    
    // Fetch bank accounts if bank transfer is selected
    if (paymentGateway === 'bank_transfer' || paymentGateway === 'bank') {
      fetchBankAccounts();
    }
  }, [currentUser, booking, paymentGateway]);
  
  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get('/api/payments/bank-accounts');
      if (response.data.success) {
        setBankAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);

    // Validate customer info
    if (!customerInfo.name || !customerInfo.phone) {
      setError('Please provide your name and phone number');
      setProcessing(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // For bank transfer, show bank details instead of redirecting
      if (paymentGateway === 'bank_transfer' || paymentGateway === 'bank') {
        const response = await axios.post('/api/payments/create-intent', {
          booking_id: booking.booking_id || booking.id,
          amount: amount,
          currency: 'PKR',
          payment_gateway: 'bank_transfer',
          customer_info: customerInfo
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data.success) {
          // Store reference number and banks in booking state
          const updatedBooking = {
            ...booking,
            reference_number: response.data.reference_number || response.data.transaction_id,
            transaction_id: response.data.transaction_id
          };
          if (setBooking) {
            setBooking(updatedBooking);
          }
          // Update bank accounts if provided
          if (response.data.banks) {
            setBankAccounts(response.data.banks);
          }
          setShowBankDetails(true);
          toast.success('Bank transfer instructions generated! Please complete the transfer.');
        } else {
          setError(response.data.message || 'Failed to initialize bank transfer');
        }
        setProcessing(false);
        return;
      }
      
      // Create payment intent with Pakistani gateway
      const response = await axios.post('/api/payments/create-intent', {
        booking_id: booking.booking_id || booking.id,
        amount: amount,
        currency: 'PKR',
        payment_gateway: paymentGateway,
        customer_info: customerInfo
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        // If payment URL is provided, redirect to gateway
        if (response.data.payment_url) {
          window.location.href = response.data.payment_url;
        } 
        // If payment data is provided (for form submission), create form and submit
        else if (response.data.payment_data) {
          const form = document.createElement('form');
          form.method = response.data.form_method || 'POST';
          form.action = response.data.payment_url || response.data.payment_data.action;
          
          Object.keys(response.data.payment_data).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = response.data.payment_data[key];
            form.appendChild(input);
          });
          
          document.body.appendChild(form);
          form.submit();
        } else {
          toast.success('Payment initiated! Redirecting to payment gateway...');
          // For gateways that redirect immediately
          setTimeout(() => {
            window.location.href = response.data.payment_url || '/payment/success';
          }, 1000);
        }
      } else {
        setError(response.data.message || 'Failed to initialize payment');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
      toast.error('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="payment-gateway-selector">
        <h3>Select Payment Gateway</h3>
        <div className="gateway-options">
          <label className="gateway-option">
            <input
              type="radio"
              value="paypro"
              checked={paymentGateway === 'paypro'}
              onChange={(e) => setPaymentGateway(e.target.value)}
            />
            <div className="gateway-info">
              <strong>PayPro</strong>
              <span>Cards, Mobile Wallets, Bank Transfer</span>
            </div>
          </label>
          
          <label className="gateway-option">
            <input
              type="radio"
              value="jazzcash"
              checked={paymentGateway === 'jazzcash'}
              onChange={(e) => setPaymentGateway(e.target.value)}
            />
            <div className="gateway-info">
              <strong>JazzCash</strong>
              <span>JazzCash Wallet, Cards</span>
            </div>
          </label>
          
          <label className="gateway-option">
            <input
              type="radio"
              value="easypaisa"
              checked={paymentGateway === 'easypaisa'}
              onChange={(e) => setPaymentGateway(e.target.value)}
            />
            <div className="gateway-info">
              <strong>EasyPaisa</strong>
              <span>EasyPaisa Wallet, Cards, OTC</span>
            </div>
          </label>
          
          <label className="gateway-option">
            <input
              type="radio"
              value="bank_transfer"
              checked={paymentGateway === 'bank_transfer'}
              onChange={(e) => setPaymentGateway(e.target.value)}
            />
            <div className="gateway-info">
              <strong>Bank Transfer</strong>
              <span>Direct Bank Transfer, IBAN, Account Number</span>
            </div>
          </label>
        </div>
      </div>

      {showBankDetails && (
        <div className="bank-transfer-details">
          <h3>Bank Transfer Instructions</h3>
          <div className="bank-instructions">
            <p><strong>Amount:</strong> PKR {amount.toFixed(2)}</p>
            <p><strong>Reference Number:</strong> {booking?.reference_number || 'N/A'}</p>
            <p className="important-note">⚠️ Please include the reference number in your transfer!</p>
            
            <div className="bank-accounts-list">
              <h4>Transfer to any of these accounts:</h4>
              {bankAccounts.map((bank, index) => (
                <div key={index} className="bank-account-card">
                  <h5>{bank.name}</h5>
                  <div className="account-details">
                    <p><strong>Account Title:</strong> {bank.accountTitle}</p>
                    <p><strong>Account Number:</strong> {bank.accountNumber}</p>
                    <p><strong>IBAN:</strong> {bank.iban}</p>
                    <p><strong>Branch:</strong> {bank.branch}</p>
                    {bank.swiftCode && <p><strong>SWIFT:</strong> {bank.swiftCode}</p>}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="transfer-steps">
              <h4>Steps to Complete Payment:</h4>
              <ol>
                <li>Transfer PKR {amount.toFixed(2)} to any bank account above</li>
                <li>Include reference number: <strong>{booking.reference_number || booking.transaction_id || 'N/A'}</strong> in transfer description</li>
                <li>Take a screenshot or note of your transaction receipt</li>
                <li>Your booking will be confirmed once we verify the payment (usually within 24 hours)</li>
                <li>You will receive a confirmation email once payment is verified</li>
              </ol>
            </div>
            
            <button 
              type="button" 
              className="close-bank-details"
              onClick={() => {
                setShowBankDetails(false);
                onSuccess({ status: 'pending', type: 'bank_transfer' });
              }}
            >
              I Understand, Close
            </button>
          </div>
        </div>
      )}

      <div className="customer-info-section">
        <h3>Your Information</h3>
        <div className="form-group">
          <label>Full Name *</label>
          <input
            type="text"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
            required
            placeholder="Enter your full name"
          />
        </div>
        
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
            placeholder="your@email.com"
          />
        </div>
        
        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
            required
            placeholder="03001234567"
            pattern="[0-9]{11}"
          />
        </div>
      </div>

      {error && <div className="payment-error">{error}</div>}

      <button
        type="submit"
        disabled={processing}
        className="pay-button"
      >
        {processing ? 'Processing...' : `Pay PKR ${amount.toFixed(2)}`}
      </button>
      
      <p className="payment-note">
        🔒 You will be redirected to a secure payment gateway to complete your payment.
      </p>
    </form>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId } = useParams();
  const { currentUser } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    // Get booking from location state or fetch from API
    if (location.state?.booking) {
      setBooking(location.state.booking);
      setAmount(parseFloat(location.state.booking.total_amount || location.state.amount || 0));
      setLoading(false);
    } else if (bookingId) {
      // Fetch booking from API
      fetchBooking();
    } else {
      toast.error('No booking information found');
      navigate('/');
    }
  }, [bookingId, location.state, navigate]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`/api/bookings/${bookingId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setBooking(response.data.data);
        setAmount(parseFloat(response.data.data.total_amount || 0));
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    // Redirect to success page
    navigate('/payment/success', {
      state: {
        booking: booking,
        payment: paymentIntent
      }
    });
  };

  if (loading) {
    return (
      <div className="checkout-loading">
        <div className="spinner"></div>
        <p>Loading checkout...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="checkout-error">
        <h2>Booking not found</h2>
        <button onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Complete Your Booking</h1>
          <button className="close-button" onClick={() => navigate('/')}>×</button>
        </div>

        <div className="checkout-content">
          <div className="booking-summary">
            <h2>Booking Summary</h2>
            <div className="summary-item">
              <span>Hotel:</span>
              <span>{booking.hotel_name || 'Hotel'}</span>
            </div>
            {booking.rooms && booking.rooms.length > 0 && (
              <div className="summary-item">
                <span>Reserved Rooms:</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  {booking.rooms.map((room, index) => (
                    <span key={index}>
                      Room {room.room_number} ({room.room_type})
                      {room.status === 'reserved' && <span style={{ color: '#ff9800', marginLeft: '8px' }}>⏱ Reserved</span>}
                      {room.status === 'booked' && <span style={{ color: '#4caf50', marginLeft: '8px' }}>✓ Booked</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(!booking.rooms || booking.rooms.length === 0) && (
              <div className="summary-item">
                <span>Rooms:</span>
                <span>{booking.rooms_reserved || 'Processing...'}</span>
              </div>
            )}
            <div className="summary-item">
              <span>Check-in:</span>
              <span>{new Date(booking.check_in_date || booking.checkin_date || booking.checkInDate).toLocaleDateString()}</span>
            </div>
            <div className="summary-item">
              <span>Check-out:</span>
              <span>{new Date(booking.check_out_date || booking.checkout_date || booking.checkOutDate).toLocaleDateString()}</span>
            </div>
            <div className="summary-item">
              <span>Guests:</span>
              <span>{booking.adults || booking.guest_count || booking.totalGuests || 1}</span>
            </div>
            {booking.special_requests && (
              <div className="summary-item">
                <span>Special Requests:</span>
                <span>{booking.special_requests}</span>
              </div>
            )}
            <div className="summary-total">
              <span>Total Amount:</span>
              <span className="amount">PKR {amount.toFixed(2)}</span>
            </div>
          </div>

          <div className="payment-section">
            <h2>Payment Information</h2>
            <CheckoutForm 
              booking={booking} 
              amount={amount} 
              onSuccess={handlePaymentSuccess}
              setBooking={setBooking}
            />
          </div>
        </div>

        <div className="checkout-footer">
          <p>🔒 Your payment information is secure and encrypted</p>
          <p>By completing this payment, you agree to our Terms and Conditions</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;

